import { SolIcon } from '@sol/icons';
import { SolCheckboxValue } from '../core/sol-checkbox';
import { SolType } from '../core/sol-type';
import { SolTextColor } from '../core/sol-text-color';
import { SolViewId } from '../core/sol-view-id';
import { SolViewOptions } from '../core/sol-view-options';
import { SolViewTextOptions } from '../core/sol-view-text-options';
import { SolTableDef } from './sol-table-def';
import { SolTableColumnDef } from './sol-table-column-def';
import { SolTableSort } from './sol-table-sort';
import { SolTableSortEvent } from './sol-table-sort-event';
import { SolTableGroup } from './sol-table-group';
import { SolTableActionGroup } from './sol-table-action-group';
import { SolTableActionState } from './sol-table-action-state';
import { SolTableAction } from './sol-table-action';
import {
  SolMenuGroup,
  SolMenuItem,
} from '../../components/popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SolActionBarData } from '../../components/overview/sol-action-bar/sol-action-bar.state';
import { SolViewData } from '../../components/view/sol-view/sol-view.component';
import { ObjectTools } from '../../tools/object-tools';
import { TableTools } from '../../tools/table-tools';

export class SolTable<D> {
  private constructor(
    // Raw data
    public readonly definition: SolTableDef<D>,
    public readonly items: D[],
    public readonly groups: SolTableGroup<D>[],
    public readonly selection: string[],
    public readonly sorts: SolTableSort[],
    public readonly visibleColumns: SolVisibleColumn[],
    public readonly currentRowId: string | undefined,
    /** The row the user activated; it follows `currentRowId` and is dropped once its row is gone. */
    public readonly activeRowId: string | undefined,
    public readonly expandedRowId: string | undefined,
    public readonly expandableRowIds: string[] | undefined,
    public readonly viewOptionsMap: Record<string, any>,

    // Derived
    public readonly tableStyle: TableStyle,
    public readonly columns: Column<D, any>[],
    public readonly visibleGroups: Group<D>[],
    public readonly selectAllValue: SolCheckboxValue,
    public readonly hasActions: boolean,
    public readonly hasActionsColumn: boolean,
    public readonly navigable: boolean,
    public readonly contextMenu: SolMenuGroup[],
    public readonly actionBarData: SolActionBarData | undefined,

    // Component-internal
    public readonly oldExpandedRowId: string | undefined,
    public readonly columnResize: SolTableColumnResize | undefined,
    public readonly columnResizeGuide: number | undefined,
  ) {}

  public static empty<D>(): SolTable<D> {
    return SolTable.of<D>(SolTableDef.of<D>(() => '', SolType.any, []));
  }

  /** A new table opens on the sort its definition declares; a caller that knows better calls `withSorts`. */
  public static of<D>(definition: SolTableDef<D>, items: D[] = []): SolTable<D> {
    return new SolTable<D>(
      definition,
      items,
      [],
      [],
      definition.defaultSort === undefined ? [] : [definition.defaultSort],
      [],
      undefined,
      undefined,
      undefined,
      undefined,
      {},
      {},
      [],
      [],
      SolCheckboxValue.deselected,
      false,
      false,
      false,
      [],
      undefined,
      undefined,
      undefined,
      undefined,
    ).recalculate();
  }

  public toSortCode(): string | undefined {
    const sortCode = this.sorts
      .map(sort => (sort.direction === 'desc' ? `-${sort.columnId}` : sort.columnId))
      .join(',');
    return sortCode || undefined;
  }

  public get isEmpty(): boolean {
    return this.items.length === 0;
  }

  public withDefinition(definition: SolTableDef<D>): SolTable<D> {
    return this.copy({ definition }).recalculate();
  }

  public withItems(items: D[]): SolTable<D> {
    if (this.items === items) {
      return this;
    }
    return this.copy({ items, selection: [] })
      .recalculateCurrentRowId()
      .recalculateActiveRowId()
      .recalculateExpandedRowId()
      .recalculate();
  }

  public withGroups(groups: SolTableGroup<D>[]): SolTable<D> {
    if (ObjectTools.equals(this.groups, groups)) {
      return this;
    }
    return this.copy({ groups }).recalculateRows();
  }

  public withSelection(selection: string[]): SolTable<D> {
    if (ObjectTools.equals(this.selection, selection)) {
      return this;
    }
    return this.copy({ selection }).recalculateSelectAllValue().recalculateContextMenu();
  }

  public withSort(sort: SolTableSort | undefined) {
    return this.withSorts(sort ? [sort] : []);
  }

  public withSorts(sorts: SolTableSort[]): SolTable<D> {
    if (ObjectTools.equals(this.sorts, sorts)) {
      return this;
    }
    return this.copy({ sorts }).recalculateColumns();
  }

  public withSortEvent(event: SolTableSortEvent, multiSort: boolean = false): SolTable<D> {
    if (event.direction === undefined) {
      const remainingSorts = this.sorts.filter(sort => sort.columnId !== event.columnId);
      return this.withSorts(remainingSorts);
    }

    const updatedSort: SolTableSort = {
      columnId: event.columnId,
      direction: event.direction,
    };

    if (multiSort) {
      const remainingSorts = this.sorts.filter(sort => sort.columnId !== event.columnId);
      return this.withSorts([updatedSort, ...remainingSorts]);
    }

    return this.withSort(updatedSort);
  }

  public withVisibleColumns(visibleColumns: SolVisibleColumn[]): SolTable<D> {
    if (ObjectTools.equals(this.visibleColumns, visibleColumns)) {
      return this;
    }
    const reconciled = this.copy({ visibleColumns }).recalculateCleanVisibleColumns();
    if (!reconciled.visibleColumns.some(column => column.visible) && this.definition.columns.length > 0) {
      return this;
    }
    return reconciled.recalculateColumns().recalculateRows();
  }

  public resetColumns(): SolTable<D> {
    const visibleColumns = TableTools.toVisibleColumns(this.definition);
    return this.withVisibleColumns(visibleColumns);
  }

  public canHideColumn(columnId: string): boolean {
    const column = this.definition.columns.find(candidate => candidate.id === columnId);
    if (column === undefined || !column.hideable) {
      return false;
    }
    const isVisible = this.columns.some(visible => visible.definition.id === columnId);
    if (!isVisible) {
      return true;
    }
    return this.columns.length > 1;
  }

  public withCurrentRowId(currentRowId: string | undefined): SolTable<D> {
    if (this.currentRowId === currentRowId) {
      return this;
    }
    const activeRowId = this.activeRowId === undefined ? undefined : currentRowId;
    return this.copy({ currentRowId, activeRowId }).recalculateContextMenu();
  }

  /** Activates a row and moves the cursor there, or clears it with `undefined`. */
  public withActiveRowId(activeRowId: string | undefined): SolTable<D> {
    const table = activeRowId === undefined ? this : this.withCurrentRowId(activeRowId);
    if (table.activeRowId === activeRowId) {
      return table;
    }
    return table.copy({ activeRowId });
  }

  public withExpanded(expandedRowId: string | undefined): SolTable<D> {
    if (this.expandedRowId === expandedRowId) {
      return this;
    }
    return this.copy({ expandedRowId, oldExpandedRowId: this.expandedRowId });
  }

  public withExpandableRowIds(expandableRowIds: string[] | undefined): SolTable<D> {
    if (ObjectTools.equals(this.expandableRowIds, expandableRowIds)) {
      return this;
    }
    return this.copy({ expandableRowIds });
  }

  public isExpandable(rowId: string): boolean {
    if (this.definition.clickBehavior !== 'expand') {
      return false;
    }
    return this.canExpand(rowId);
  }

  public canExpand(rowId: string): boolean {
    const allowlistOk = this.expandableRowIds === undefined || this.expandableRowIds.includes(rowId);
    return allowlistOk && this.hasRow(rowId);
  }

  public withViewOptions<O extends SolViewOptions>(
    rowIds: string[],
    columnId: string,
    viewOptions: Partial<O>,
  ): SolTable<D> {
    const viewOptionsMap: Record<string, Partial<O>> = { ...this.viewOptionsMap };
    for (const rowId of rowIds) {
      const key = `${rowId}-${columnId}`;
      viewOptionsMap[key] = viewOptions;
    }
    return this.copy({ viewOptionsMap }).recalculateRows();
  }

  public withClearViewOptions(): SolTable<D> {
    return this.copy({ viewOptionsMap: {} }).recalculateRows();
  }

  public withOldExpandedRowId(oldExpandedRowId: string | undefined): SolTable<D> {
    if (this.oldExpandedRowId === oldExpandedRowId) {
      return this;
    }
    return this.copy({ oldExpandedRowId });
  }

  public withResetColumns(): SolTable<D> {
    return this.withVisibleColumns(TableTools.toVisibleColumns(this.definition));
  }

  public withAddedColumn(columnId: string, index: number): SolTable<D> {
    const rightOfId = index === 0 ? undefined : this.columns[index - 1].definition.id;
    const existing = this.visibleColumns.find(column => column.id === columnId);
    const columnToAdd: SolVisibleColumn = {
      ...existing,
      id: columnId,
      visible: true,
    };
    const visibleColumns: SolVisibleColumn[] = [];
    for (let i = 0; i < this.visibleColumns.length; i++) {
      const current = this.visibleColumns[i];
      if (rightOfId === undefined && i === 0) {
        visibleColumns.push(columnToAdd);
      }
      if (columnToAdd.id !== current.id) {
        visibleColumns.push(current);
      }
      if (rightOfId !== undefined && rightOfId === current.id) {
        visibleColumns.push(columnToAdd);
      }
    }
    return this.withVisibleColumns(visibleColumns);
  }

  public withMovedVisibleColumn(oldIndex: number, newIndex: number): SolTable<D> {
    const columnId = this.columns[oldIndex].definition.id;
    const index = newIndex > oldIndex ? newIndex + 1 : newIndex;
    return this.withAddedColumn(columnId, index);
  }

  /**
   * `undefined` returns the column to automatic width by dropping the key, so it equals the default layout.
   * Any other width is rounded and clamped into the supported range.
   */
  public withColumnWidth(columnId: string, requestedWidth: number | undefined): SolTable<D> {
    const width = requestedWidth === undefined ? undefined : TableTools.toColumnWidth(requestedWidth);
    const current = this.visibleColumns.find(column => column.id === columnId);
    if (current === undefined || current.width === width) {
      return this;
    }
    const resized: SolVisibleColumn =
      width === undefined ? { id: current.id, visible: current.visible } : { ...current, width };
    const visibleColumns = this.visibleColumns.map(column => (column === current ? resized : column));
    const next = this.copy({ visibleColumns }).recalculateColumns();
    return (current.width === undefined) === (width === undefined) ? next : next.recalculateRows();
  }

  /**
   * `minWidth` is what the header needs to stay readable, clamped into the supported range like any width.
   * `columnLeft` is the column's left edge in the scrolled content, where the guide is measured from.
   */
  public withColumnResizeStart(
    columnId: string,
    originX: number,
    originWidth: number,
    minWidth: number,
    columnLeft: number,
  ): SolTable<D> {
    const columnResize: SolTableColumnResize = {
      columnId,
      originX,
      originWidth,
      minWidth: TableTools.toColumnWidth(minWidth),
      columnLeft,
      draftWidth: undefined,
    };
    return this.copy({ columnResize }).recalculateColumnResizeGuide();
  }

  /** Moves only the draft: the grid, columns and rows stay as they are until the drag ends. */
  public withColumnResizeMove(x: number): SolTable<D> {
    const resize = this.columnResize;
    if (resize === undefined) {
      return this;
    }
    const draftWidth =
      x === resize.originX
        ? undefined
        : TableTools.toColumnWidth(Math.max(resize.minWidth, resize.originWidth + x - resize.originX));
    return this.copy({ columnResize: { ...resize, draftWidth } }).recalculateColumnResizeGuide();
  }

  public withColumnResizeEnd(): SolTable<D> {
    const resize = this.columnResize;
    if (resize === undefined) {
      return this;
    }
    const ended = this.copy({ columnResize: undefined }).recalculateColumnResizeGuide();
    return resize.draftWidth === undefined ? ended : ended.withColumnWidth(resize.columnId, resize.draftWidth);
  }

  public withColumnResizeCancel(): SolTable<D> {
    if (this.columnResize === undefined) {
      return this;
    }
    return this.copy({ columnResize: undefined }).recalculateColumnResizeGuide();
  }

  private recalculate(): SolTable<D> {
    return this.recalculateCleanVisibleColumns()
      .recalculateColumns()
      .recalculateRows()
      .recalculateSelectAllValue()
      .recalculateContextMenu();
  }

  private recalculateCleanVisibleColumns(): SolTable<D> {
    const visibleColumns = SolTable.toReconciledVisibleColumns(this.definition.columns, this.visibleColumns);

    if (ObjectTools.equals(this.visibleColumns, visibleColumns)) {
      return this;
    }
    return this.copy({ visibleColumns });
  }

  private recalculateColumnResizeGuide(): SolTable<D> {
    const resize = this.columnResize;
    const columnResizeGuide =
      resize === undefined ? undefined : resize.columnLeft + (resize.draftWidth ?? resize.originWidth);
    return this.copy({ columnResizeGuide });
  }

  private recalculateColumns(): SolTable<D> {
    const shownColumns = this.visibleColumns.filter(cs => cs.visible);
    const visibleColumns = shownColumns.map(cs => this.definition.columns.find(column => column.id === cs.id)!);
    const gridTemplateColumns = shownColumns
      .map((cs, index) =>
        cs.width === undefined ? visibleColumns[index].layout || 'minmax(max-content, auto)' : `${cs.width}px`,
      )
      .join(' ');
    const actions = this.definition.menu || [];
    const hasActions = actions.length > 0;
    const expandable = this.definition.clickBehavior === 'expand';
    const hasActionsColumn = expandable || hasActions;
    const navigable = this.definition.clickBehavior !== 'none' || hasActions;
    const gridColumns = ['min-content', gridTemplateColumns].filter(part => part.length > 0).join(' ');
    const sortMap = new Map<string, SolTableSort>(this.sorts.map(s => [s.columnId, s]));
    return this.copy({
      tableStyle: {
        gridTemplateColumns: gridColumns,
      },
      columns: visibleColumns.map((columnDefinition, index) => {
        const sort = sortMap.get(columnDefinition.id);
        return {
          definition: columnDefinition,
          fixed: shownColumns[index].width !== undefined,
          sortable: columnDefinition.sortable,
          sorted: !!sort,
          sortIcon: SolTable.toHeaderIcon(columnDefinition, sort),
          ariaSort: SolTable.toAriaSort(columnDefinition, sort),
        };
      }),
      hasActions,
      hasActionsColumn,
      navigable,
    });
  }

  private recalculateRows(): SolTable<D> {
    const visibleGroups: Group<D>[] = [];
    const allRows = this.items.map(item => SolTable.toRow(this, item));
    let group = 0;
    if (this.groups.length === 0) {
      visibleGroups.push({
        id: '' + group++,
        rows: allRows,
      });
    } else {
      for (const groupDefinition of this.groups) {
        const rows = allRows.filter(row => groupDefinition.ids.includes(row.id));
        visibleGroups.push({
          id: '' + group++,
          name: groupDefinition.name,
          rows: rows,
        });
      }
    }
    return this.copy({ visibleGroups });
  }

  private recalculateCurrentRowId(): SolTable<D> {
    if (this.currentRowId === undefined || this.hasRow(this.currentRowId)) {
      return this;
    }
    return this.copy({ currentRowId: undefined });
  }

  private recalculateActiveRowId(): SolTable<D> {
    if (this.activeRowId === undefined || this.hasRow(this.activeRowId)) {
      return this;
    }
    return this.copy({ activeRowId: undefined });
  }

  private recalculateExpandedRowId(): SolTable<D> {
    if (this.expandedRowId === undefined || this.hasRow(this.expandedRowId)) {
      return this;
    }
    return this.copy({ expandedRowId: undefined });
  }

  private recalculateSelectAllValue(): SolTable<D> {
    const selectedCount = this.selection.length;
    const itemsCount = this.items.length;
    let selectAllValue = SolCheckboxValue.deselected;
    if (selectedCount === itemsCount && this.items.length > 0) {
      selectAllValue = SolCheckboxValue.selected;
    } else if (selectedCount > 0) {
      selectAllValue = SolCheckboxValue.indeterminate;
    }
    return this.copy({ selectAllValue });
  }

  private recalculateContextMenu(): SolTable<D> {
    const tableMenu = this.definition.menu || [];
    const rows = SolTable.getContextMenuRows(this);
    const contextMenu = SolTable.toSolMenuGroups(tableMenu, rows);
    return this.copy({
      contextMenu,
      actionBarData:
        this.selection.length === 0
          ? undefined
          : {
              count: this.selection.length,
              menu: contextMenu,
            },
    });
  }

  private hasRow(rowId: string): boolean {
    return this.items.some(item => this.definition.idFn(item) === rowId);
  }

  private copy(props: Partial<SolTable<D>>): SolTable<D> {
    return ObjectTools.copy<SolTable<D>>(this, props);
  }

  private static getContextMenuRows<D>(state: SolTable<D>): D[] {
    if (state.selection.length === 0 && state.currentRowId === undefined) {
      return [];
    }
    if (state.selection.length > 0) {
      return state.items.filter(item => {
        const id = state.definition.idFn(item);
        return state.selection.includes(id);
      });
    }
    const currentRow = state.items.find(item => {
      const id = state.definition.idFn(item);
      return state.currentRowId === id;
    });
    return !currentRow ? [] : [currentRow];
  }

  private static toRow<D>(state: SolTable<D>, item: D): Row<D> {
    const columns = state.columns;
    const rowId = state.definition.idFn(item);
    const viewOptionsMap = state.viewOptionsMap;
    const cells: Cell<any>[] = columns.map(column => {
      const columnId = column.definition.id;
      const fieldDef = state.definition.object.getType(column.definition.dataPath);
      const viewId = column.definition.viewId;
      const dataType = fieldDef ?? SolType.string;
      const isPrimitive = TableTools.isPrimitive(dataType, viewId);
      const cellOptions = viewOptionsMap[`${rowId}-${columnId}`] ?? {};
      const viewOptions = {
        ...TableTools.toPrimitiveDefaultOptions(dataType, viewId),
        ...column.definition.viewOptions,
        ...SolTable.toFixedCellOptions(column.fixed, isPrimitive),
        ...cellOptions,
      };
      const truncated = TableTools.hasTextOptions(isPrimitive, dataType)
        ? TableTools.toOption(SolViewId.textField, viewOptions, 'truncated')
        : undefined;
      const color = TableTools.hasTextOptions(isPrimitive, dataType)
        ? TableTools.toOption(SolViewId.textField, viewOptions, 'color')
        : undefined;
      const format =
        isPrimitive && dataType.isA(SolType.date)
          ? TableTools.toOption(SolViewId.date, viewOptions, 'format')
          : undefined;
      const viewData = isPrimitive
        ? undefined
        : ({
            dataType,
            viewId,
            mode: viewOptions.mode === 'edit' ? 'edit' : 'read',
            value: state.definition.object.get(column.definition.dataPath, item),
            viewOptions,
            messages: [],
            immediate: false,
            focusIndex: -1,
          } satisfies SolViewData<any, any>);
      return {
        id: columnId,
        type: dataType,
        viewId: column.definition.viewId,
        viewOptions,
        value: state.definition.object.get(column.definition.dataPath, item),
        isPrimitive,
        viewData,
        fixed: column.fixed,
        truncated,
        color,
        format,
      };
    });
    const tableMenu = state.definition.menu || [];
    const menu: SolMenuGroup[] = this.toSolMenuGroups(tableMenu, [item]);
    return {
      id: rowId,
      item: item,
      cells: cells,
      menu: menu,
    };
  }

  private static toSolMenuGroups<D>(tableMenu: SolTableActionGroup<D>[], items: D[]): SolMenuGroup[] {
    if (items.length === 0) {
      return [];
    }
    return tableMenu.map(menuGroup => this.toSolMenuGroup(menuGroup, items)).filter(group => group.items.length > 0);
  }

  private static toSolMenuGroup<D>(menuGroup: SolTableActionGroup<D>, items: D[]): SolMenuGroup {
    return {
      name: menuGroup.name,
      items: menuGroup.items
        .map(action => this.toSolMenuItem(action, items))
        .filter(action => action !== undefined)
        .map(action => action as SolMenuItem),
    };
  }

  private static toSolMenuItem<D>(action: SolTableAction<D>, items: D[]): SolMenuItem | undefined {
    const state = action.stateFn(items);
    if (state === SolTableActionState.hidden) {
      return undefined;
    }

    return {
      ...action,
      disabled: state === SolTableActionState.disabled,
    } as SolMenuItem;
  }

  private static toHeaderIcon(
    definition: SolTableColumnDef<any, any>,
    sort: SolTableSort | undefined,
  ): SolIcon | undefined {
    if (sort !== undefined) {
      return sort.direction === 'asc' ? SolIcon.arrow_up : SolIcon.arrow_down;
    } else if (definition.sortable) {
      return SolIcon.arrows_up_down;
    }
    return undefined;
  }

  private static toAriaSort(
    definition: SolTableColumnDef<any, any>,
    sort: SolTableSort | undefined,
  ): 'ascending' | 'descending' | 'none' | undefined {
    if (sort !== undefined) {
      return sort.direction === 'asc' ? 'ascending' : 'descending';
    }
    if (definition.sortable) {
      return 'none';
    }
    return undefined;
  }

  private static toReconciledVisibleColumns(
    definitionColumns: SolTableColumnDef<any, any>[],
    currentVisibleColumns: SolVisibleColumn[],
  ): SolVisibleColumn[] {
    const definitionColumnIds = definitionColumns.map(column => column.id);
    const definitionColumnIdSet = new Set(definitionColumnIds);
    const visibleColumns: SolVisibleColumn[] = [];
    const visibleColumnIdSet = new Set<string>();

    // Keep the current order for known columns and drop stale/duplicate ids.
    for (const current of currentVisibleColumns) {
      if (!definitionColumnIdSet.has(current.id) || visibleColumnIdSet.has(current.id)) {
        continue;
      }
      visibleColumns.push(SolTable.toSupportedWidth(current));
      visibleColumnIdSet.add(current.id);
    }

    // Insert missing columns next to their nearest right-side neighbor from the definition.
    for (let i = 0; i < definitionColumns.length; i++) {
      const definitionColumn = definitionColumns[i];
      if (visibleColumnIdSet.has(definitionColumn.id)) {
        continue;
      }

      const insertIndex = SolTable.toMissingColumnInsertIndex(definitionColumnIds, i, visibleColumns);
      visibleColumns.splice(insertIndex, 0, {
        id: definitionColumn.id,
        visible: definitionColumn.visible ?? true,
      });
      visibleColumnIdSet.add(definitionColumn.id);
    }

    return visibleColumns;
  }

  /** A resized column holds its content to one line: primitive text truncates, a view gets a fixed width. */
  private static toFixedCellOptions(fixed: boolean, isPrimitive: boolean): Partial<SolViewTextOptions> {
    if (!fixed) {
      return {};
    }
    return isPrimitive ? { truncated: true } : { fixedWidth: true };
  }

  /** A width outside the supported range, say from an older stored layout, reads as automatic. */
  private static toSupportedWidth(column: SolVisibleColumn): SolVisibleColumn {
    if (column.width === undefined || TableTools.isColumnWidth(column.width)) {
      return column;
    }
    return { id: column.id, visible: column.visible };
  }

  private static toMissingColumnInsertIndex(
    definitionColumnIds: string[],
    missingColumnIndex: number,
    visibleColumns: SolVisibleColumn[],
  ): number {
    for (let i = missingColumnIndex + 1; i < definitionColumnIds.length; i++) {
      const rightColumnId = definitionColumnIds[i];
      const visibleIndex = visibleColumns.findIndex(column => column.id === rightColumnId);
      if (visibleIndex !== -1) {
        return visibleIndex;
      }
    }
    return visibleColumns.length;
  }
}

/** `width` is a fixed pixel width set by resizing; a column without the key sizes itself. */
export type SolVisibleColumn = {
  id: string;
  visible: boolean;
  width?: number;
};

/** A header-edge drag in progress. `draftWidth` is `undefined` until the pointer leaves where it started. */
export interface SolTableColumnResize {
  readonly columnId: string;
  readonly originX: number;
  readonly originWidth: number;
  readonly minWidth: number;
  readonly columnLeft: number;
  readonly draftWidth: number | undefined;
}

/**
 * A table's layout as it is persisted: which columns it shows, in what order, how wide, and the sort
 * it opens on. Hidden columns stay in `columns` so that unhiding one puts it back where it was.
 */
export interface SolTableLayout {
  readonly columns: readonly SolVisibleColumn[];
  readonly sort: SolTableSort | undefined;
}

export interface Column<S, R> {
  definition: SolTableColumnDef<R, any>;
  fixed: boolean;
  sortable: boolean;
  sorted: boolean;
  sortIcon: SolIcon | undefined;
  ariaSort: 'ascending' | 'descending' | 'none' | undefined;
}

export interface Group<S> {
  id: string;
  name?: string;
  rows: Row<S>[];
}

export interface Row<S> {
  id: string;
  item: S;
  cells: Cell<any>[];
  menu: SolMenuGroup[];
}

export interface Cell<R> {
  id: string;
  type: SolType<R>;
  viewId?: SolViewId<any>;
  viewOptions: any;
  value: R | undefined;
  isPrimitive: boolean;
  viewData?: SolViewData<any, any>;
  fixed: boolean;

  // Native view options
  truncated?: boolean;
  color?: SolTextColor;
  format?: string;
}

export interface TableStyle {
  gridTemplateColumns?: string;
}
